import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  CargoPreviewPlanOutDTO,
  CarInfoPreviewOutDTO,
  CustomsStatusEnum,
  EntityTypeEnum,
  ExpeditionPreviewOutDTO,
  ExpeditionStatusEnum,
  ExpeditionTypeEnum,
  ExpedtionListGroupedFilterDTO,
  InvoiceStateEnum,
  LovTransportationTypeOutDTO,
  TransportationTypeEnum,
  FeaturesEnum,
  IntentEnum,
  OrderPreviewExpeditionListOutDTO,
  Pageable,
  PermissionTypesEnum,
  PriceDTO,
  TableKeyEnum,
  ValidDistributionEnum,
} from '@api/ringil-api.v2';
import { enc, isNotBlank, isNotNullOrUndefined, isNullOrUndefined, useLocalStorage } from '@utils/utils';
import { dayjsFromApiUtcInTz, getBrowserTimezone, stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import { formatTimeslotInterval } from '@utils/timeslotDateTime';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import SupplierNotificationBadge from '@components/RingilComponents/badges/SupplierNotificationBadge';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import { useCreateMultiduplicationSidebar } from '@components/ringil3/Features/Expedition/MultiduplicationSidebar/MultiduplicationSidebar.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Button } from 'primereact/button';
import { ApiContext } from '@api/api';
import { Toast } from 'primereact/toast';
import useNavigation from '@hooks/useNavigation';
import { useTranslation } from 'react-i18next';
import useResponsiblePersons from '@hooks/responsible-persons/responsible-persons.tsx';
import useCustomerCarriers from '@hooks/customer-carrier/customer-carriers.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useMAExpUpdIntInformation } from '@components/obsolete/MAExpUpdIntInformation/MAExpUpdIntInformation.tsx';
import { useMAExp } from '@components/obsolete/MAExp/MAExp.tsx';
import { mergeDisabledReason } from '@app/pages/expedition/create/mergeEligibility.ts';
import { useDachserExport } from '@components/obsolete/MassActionDachserExport/MassActionDachser.tsx';
import UploadAttachmentDialog from '@components/ringil3/Features/Attachments/UploadAttachmentDialog.tsx';
import { Col } from '@components/styles.tsx';
import { Link } from 'react-router-dom';
import ExpeditionStateSpan from '@components/obsolete/StateSpan/ExpeditionStateSpan.tsx';
import ExpeditionMoreButtons from '@components/ringil3/Features/Expedition/MoreButton/ExpeditionMoreButtons.tsx';
import DuplicateExpeditionDialog from '@components/obsolete/DuplicateExpedition/DuplicateExpeditionDialog.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import clsx from 'clsx';
import AlertCircle from '@icons/AlertCircle.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import SvgStar from '@icons/star-inverted.svg?react';
import SvgFile from '@icons/file-inverted.svg?react';
import SvgImportExpeditions from '@icons/file-inverted.svg?react';
import SvgCheckSquare from '@icons/check-square.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgLink from '@icons/link.svg?react';
import { useMediaQuery } from 'react-responsive';
import SvgRoute from '@components/ringil3/icons/heading/Route';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgOrder from '@icons/order.svg?react';
import SvgUser from '@icons/user-inverted.svg?react';
import SvgMoney from '@icons/money-inverted.svg?react';
import SvgLayers from '@icons/layers-inverted.svg?react';
import SvgNumber from '@icons/number.svg?react';
import SvgCustoms from '@icons/Customs.svg?react';
import { AiChatClassificationButton, getIntentIconColorClass, intentEnumIcons } from '@components/ai/AiChatClassificationButton.tsx';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgClose from '@icons/close.svg?react';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import SafeHtml from '@components/RingilComponents/RichTextEditor/SafeHtml.tsx';
import { noteToPlainText } from '@components/RingilComponents/RichTextEditor/sanitizeNoteHtml';
import { getTransportIcon, joinLegInfo } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';
import PriceTag from '@components/RingilComponents/tags/priceTag/PriceTag.tsx';
import { formatPrice, sumPriceDTOs } from '@utils/priceUtils.ts';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import { truncateFileName } from '@utils/fileUtils.ts';
import dayjs from 'dayjs';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import RingilDataTable, {
  ColumnConfig,
  DateTimeFormatType,
  FilterConfig,
  FilterType,
  MassActionConfig,
  OptionType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilDataTableV3, { TableViewsConfig } from '@components/RingilDataTable/v3/RingilDataTableV3.tsx';
import { buildExpeditionFields } from './v3/expeditionFields';
import { EXPEDITION_DEFAULT_COLUMNS, EXPEDITION_FIELD_GROUPS, EXPEDITION_PRESETS } from './v3/expeditionFieldKeys';
import LegacyVersionSwitch from '@components/framework/LegacyVersionSwitch.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import CountryFlag from '@components/framework/CountryFlag/CountryFlag.tsx';
import { downloadBase64File } from '@utils/download';
import { NO_LABEL_FILTER_VALUE } from '@components/framework/tags/LabelsTag.tsx';
import { InfoTagEnum } from './infoTags';
import ExpeditionDetail from '@components/ringil3/Features/Expedition/ExpeditionDetail';

interface ComponentProps {
  customerId: string;
  shipmentNumber?: string;
}

interface MassActionExpedition {
  expeditionId: string;
  applicationId: string;
  companyId: string;
  locationFrom: string;
  locationTo: string;
  state: ExpeditionStatusEnum;
  isPublished: boolean;
  isManualExpedition: boolean;
}

/**
 * Tri-state the archived dropdown stores into `showArchived`. Resolved back to the boolean the API
 * expects (or omitted) at fetch time in normalizeExpeditionFilter: ARCHIVED → true, NOT_ARCHIVED →
 * false, ALL → omit the field entirely so the backend returns both archived and non-archived shipments.
 */
export enum ArchivedFilterStateEnum {
  ALL = 'all',
  ARCHIVED = 'archived',
  NOT_ARCHIVED = 'notArchived',
}

/**
 * Tri-state the reservation dropdown stores into `reservationState`. Resolved back to the
 * `showWithoutLinkedReservation` boolean the API expects (or omitted) at fetch time in
 * normalizeExpeditionFilter: WITHOUT_RESERVATION → true (shipments having a location without linked
 * reservation which can still be filled), WITH_RESERVATION → false (all others), ALL → omit the field.
 */
export enum ReservationFilterStateEnum {
  ALL = 'all',
  WITHOUT_RESERVATION = 'withoutReservation',
  WITH_RESERVATION = 'withReservation',
}

const NO_INVOICE_STATE_FILTER_VALUE = 'NO_INVOICE_STATE';

export interface AllExpeditionCustomerFilter {
  applicationId?: string;
  responsiblePersonIds?: string[];
  carrierIds?: string[];
  statusEnums?: ExpeditionStatusEnum[];
  loadingDateFromISO?: string;
  loadingDateToISO?: string;
  unloadingDateFromISO?: string;
  unloadingDateToISO?: string;
  expeditionType?: ExpeditionTypeEnum;
  loadingLocationName?: string;
  loadingCountryCode?: string;
  unLoadingLocationName?: string;
  unloadingCountryCode?: string;
  validDistribution?: ValidDistributionEnum;
  labelIds?: string[];
  /** Form field driving the archived dropdown. Resolved to `showArchived` (or omitted) in normalizeExpeditionFilter. */
  archivedState?: ArchivedFilterStateEnum;
  /** API output only — derived from `archivedState` at fetch time; not bound to any form control. */
  showArchived?: boolean;
  /** Form field driving the reservation dropdown. Resolved to `showWithoutLinkedReservation` (or omitted) in normalizeExpeditionFilter. */
  reservationState?: ReservationFilterStateEnum;
  /** API output only — derived from `reservationState` at fetch time; not bound to any form control. */
  showWithoutLinkedReservation?: boolean;
  pairingSymbol?: string;
  trackingNumber?: string;
  withoutPairingSymbol?: boolean;
  aiDispatcherChanged?: boolean;
  invoiceStates?: string[];
  countryCode?: string;
  allLocationName?: string;
  folderIds?: string[];
  shipmentNumber?: string;
  showFavourite?: boolean;
  mrnNumber?: string;
  referenceOrderId?: string;
  createdByIds?: string[];
  transportationType?: TransportationTypeEnum[];
  customsStatus?: CustomsStatusEnum[];
  userTimezoneId: string;
}

const initialAllExpeditionCustomerFilter: AllExpeditionCustomerFilter = {
  applicationId: undefined,
  responsiblePersonIds: [],
  carrierIds: [],
  statusEnums: [],
  loadingDateFromISO: undefined,
  loadingDateToISO: undefined,
  unloadingDateFromISO: undefined,
  unloadingDateToISO: undefined,
  expeditionType: undefined,
  loadingLocationName: undefined,
  unLoadingLocationName: undefined,
  validDistribution: undefined,
  labelIds: [],
  pairingSymbol: undefined,
  trackingNumber: undefined,
  withoutPairingSymbol: false,
  aiDispatcherChanged: false,
  invoiceStates: [],
  countryCode: undefined,
  allLocationName: undefined,
  folderIds: [],
  shipmentNumber: undefined,
  loadingCountryCode: undefined,
  unloadingCountryCode: undefined,
  showFavourite: false,
  mrnNumber: undefined,
  createdByIds: [],
  transportationType: [],
  userTimezoneId: getBrowserTimezone(),
};

export interface SavedFilter {
  id: string;
  name: string;
  data: AllExpeditionCustomerFilter;
  sort: { sortField: string; sortDirection: 'ASC' | 'DESC' };
}

const ResponsiveExpeditionList: React.FC<ComponentProps> = () => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();

  const { cuListExpeditionsV3, getUserExpeditionFilters, saveUserExpeditionFilters, deleteUserFilter, downloadAttachment, lovTransportationTypes } =
    useContext(ApiContext);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const expeditionListRwFlag = useSecuredFlag('expedition-list-rw', {
    kind: 'action',
    description: 'Manage shipments in the list (duplicate, more actions, bulk actions)',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_RW],
  });
  const expeditionExtraCostsFlag = useSecuredFlag('expedition-list-extra-costs', {
    kind: 'visibility',
    description: 'See extra costs on shipments in the list',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_LIST_RW, PermissionTypesEnum.PERMISSION_TIMESLOT_LIST_READ],
  });
  const expeditionPriceFlag = useSecuredFlag('expedition-list-price', {
    kind: 'visibility',
    description: 'See shipment price in the list',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_PRICE],
  });
  const expeditionCreateFlag = useSecuredFlag('expedition-list-create', {
    kind: 'action',
    description: 'Create new shipment / import shipments',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE],
  });
  const shipmentImportFlag = useSecuredFlag('feature-shipment-manual-import', {
    kind: 'feature',
    description: 'Shipment manual-import is enabled for the company',
    features: [FeaturesEnum.SHIPMENT_MANUAL_IMPORT],
  });
  const aiClassificationFlag = useSecuredFlag('feature-ai-chat-classification', {
    kind: 'feature',
    description: 'AI chat classification is enabled for the company',
    features: [FeaturesEnum.AI_CHAT_CLASSIFICATION],
  });
  const aiDispatcherFlag = useSecuredFlag('feature-ai-dispatcher', {
    kind: 'feature',
    description: 'AI Dispatcher is enabled for the company',
    features: [FeaturesEnum.AI_DISPATCHER],
  });
  const mergeExpeditionsFlag = useSecuredFlag('feature-expedition-merge', {
    kind: 'feature',
    description: 'Merging several shipments into one is enabled for the company',
    features: [FeaturesEnum.EXPEDITION_MERGE],
  });
  const multiduplicationFlag = useSecuredFlag('mass-action-multiduplication', {
    kind: 'action',
    description: 'Bulk duplication via jobs center (company feature + dedicated permission)',
    features: [FeaturesEnum.MULTIDUPLICATION_V3],
    permissions: [PermissionTypesEnum.PERMISSION_MULTIDUPLICATION_V3],
  });
  const dachserExportFlag = useSecuredFlag('feature-dachser-xml-export', {
    kind: 'feature',
    description: 'Dachser XML export is enabled for the company',
    features: [FeaturesEnum.DACHSER_XML_EXPORT],
  });
  const attachmentNameFilterFlag = useSecuredFlag('expedition-list-attachment-name-filter', {
    kind: 'visibility',
    description: 'Filter shipments by attachment name',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_READ, PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_ATTACHMENTS_RW],
  });

  const internalInfoFilterFlag = useSecuredFlag('expedition-list-internal-info-filters', {
    kind: 'visibility',
    description: 'Filter shipments without a pairing symbol and by invoice state',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_INTERNALINFO_READ, PermissionTypesEnum.PERMISSION_EXPEDITION_DETAIL_TAB_INTERNALINFO_RW],
  });

  const responsiblePersons = useResponsiblePersons();
  const { carriers } = useCustomerCarriers();
  const [visibleUploadDialog, setVisibleUploadDialog] = useState(false);
  const [duplicateExpedition, setDuplicateExpedition] = useState<ExpeditionPreviewOutDTO | null>(null);
  const toast = useRef(null);

  const navigation = useNavigation();
  const [groupedFilters, setGroupedFilters] = useState<ExpedtionListGroupedFilterDTO | null>(null);
  const [transportationTypeLov, setTransportationTypeLov] = useState<LovTransportationTypeOutDTO[]>([]);

  const { setVisibleMAExpeditionIntInfo, MassActionExpeditionUpdateInternalInformation } = useMAExpUpdIntInformation();
  const { setVisible: setVisibleMA, MassActionExpedition } = useMAExp();
  const { setVisible: setVisibleDachserExport, MassActionDachser } = useDachserExport();

  const initialExpandedInfoTags: Record<InfoTagEnum, boolean> = Object.values(InfoTagEnum).reduce(
    (acc, key) => ({ ...acc, [key]: false }),
    {} as Record<InfoTagEnum, boolean>,
  );

  const [expandedInfoTags, setExpandedInfoTags] = useLocalStorage<Record<InfoTagEnum, boolean>>('EXPEDITION_LIST_EXPANDED_INFO_TAGS', initialExpandedInfoTags);

  const [expandedLabels, setExpandedLabels] = useLocalStorage('EXPEDITION_LIST_EXPANDED_LABELS', false);

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const timezoneIdToUse = loggedUser.defaultTimezoneId || 'Europe/Prague';

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: ExpeditionPreviewOutDTO[]; clear: () => void }>(null);

  const expeditionExportFlag = useSecuredFlag('expedition-export', {
    kind: 'action',
    description: 'Export expeditions (generic export API)',
    features: [FeaturesEnum.GENERIC_EXPORT_API],
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_EXPORT],
  });

  useEffect(() => {
    lovTransportationTypes(d => setTransportationTypeLov(d));
  }, []);

  function pragueStartOfDayISO(dateStr?: string | null): string | undefined {
    if (!dateStr) return undefined;
    return dayjs.tz(dateStr, 'Europe/Prague').startOf('day').toISOString(); // např. 2025-01-11T00:00:00+01:00
  }

  const normalizeExpeditionFilter = (value?: AllExpeditionCustomerFilter): AllExpeditionCustomerFilter => {
    const base = {
      ...initialAllExpeditionCustomerFilter,
      ...(value ?? {}),
      userTimezoneId: getBrowserTimezone(),
    };

    // Tri-state archived filter → boolean the API expects (or omit it). `archivedState` is the form
    // field; fall back to a legacy `showArchived` boolean (older saved/URL filters) and default to
    // "not archived" so the default view keeps hiding archived shipments.
    const archivedState = base.archivedState ?? (base.showArchived === true ? ArchivedFilterStateEnum.ARCHIVED : ArchivedFilterStateEnum.NOT_ARCHIVED);

    const result: AllExpeditionCustomerFilter = {
      ...base,
      loadingDateFromISO: pragueStartOfDayISO(base.loadingDateFromISO),
      loadingDateToISO: pragueStartOfDayISO(base.loadingDateToISO || base.loadingDateFromISO),
      unloadingDateFromISO: pragueStartOfDayISO(base.unloadingDateFromISO),
      unloadingDateToISO: pragueStartOfDayISO(base.unloadingDateToISO || base.unloadingDateFromISO),
    };

    // `archivedState` is UI-only; never send it. ALL → omit `showArchived` so the backend returns
    // both archived and non-archived shipments.
    delete result.archivedState;
    if (archivedState === ArchivedFilterStateEnum.ALL) {
      delete result.showArchived;
    } else {
      result.showArchived = archivedState === ArchivedFilterStateEnum.ARCHIVED;
    }

    // Tri-state reservation filter → `showWithoutLinkedReservation` boolean (or omit it). Defaults
    // to ALL so the list is unfiltered unless the user picks a state.
    const reservationState = base.reservationState ?? ReservationFilterStateEnum.ALL;
    delete result.reservationState;
    if (reservationState === ReservationFilterStateEnum.ALL) {
      delete result.showWithoutLinkedReservation;
    } else {
      result.showWithoutLinkedReservation = reservationState === ReservationFilterStateEnum.WITHOUT_RESERVATION;
    }

    // Stored filters (saved, URL, localStorage) can still carry these for a user who cannot see the
    // controls; the backend answers them with an empty list, so drop them instead.
    if (!internalInfoFilterFlag.granted) {
      delete result.withoutPairingSymbol;
      delete result.invoiceStates;
    }
    if (!aiDispatcherFlag.granted) {
      delete result.aiDispatcherChanged;
    }
    return result;
  };

  const loadDataApi = (filter: AllExpeditionCustomerFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<ExpeditionPreviewOutDTO>) => void) => {
    // This is the single normalization point (date → Prague start-of-day ISO + defaults). The table
    // is NOT given a `normalizeFilter` prop, so the filter arrives here raw and is normalized exactly once.
    // Local AllExpeditionCustomerFilter is a superset of the generated one (enum field types differ)
    cuListExpeditionsV3(normalizeExpeditionFilter(filter) as never, pageable, data => {
      setGroupedFilters(data.groupedFilter);
      onSuccess(data.expeditions);
    });
  };

  // Map a table row to the lighter shape the mass-action dialogs consume.
  const toMassActionExpedition = (row: ExpeditionPreviewOutDTO): MassActionExpedition => ({
    expeditionId: row.id,
    companyId: row.companyId,
    applicationId: row.applicationId,
    locationFrom: row.locations[0]?.name,
    locationTo: row.locations[row.locations.length - 1]?.name,
    state: row.expeditionStatus,
    isPublished: row.isPublished,
    isManualExpedition: row.isManualExpedition,
  });

  const selectedMassActions = (): MassActionExpedition[] => (tableRef.current?.selectedObjects ?? []).map(toMassActionExpedition);

  const toggleInfoExpansion = (key: InfoTagEnum) => {
    setExpandedInfoTags(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const setAllTagsExpanded = (expanded: boolean) => {
    setExpandedInfoTags(_ => {
      const allInfoTagKeys = Object.values(InfoTagEnum);
      const newState = {} as Record<InfoTagEnum, boolean>;
      allInfoTagKeys.forEach(key => {
        newState[key] = expanded;
      });
      return newState;
    });
  };

  const toggleLabelsExpansion = () => {
    setExpandedLabels(prev => !prev);
  };

  const idAndResponsiblePersonTemplate = (row: ExpeditionPreviewOutDTO, isMobileCard?: boolean, toggleExpandedRow?: (row: ExpeditionPreviewOutDTO) => void) => {
    return (
      <>
        <span
          data-testid={`customer-expeditions-row-${row.applicationId}`}
          data-application-id={row.applicationId}
          data-expedition-id={row.id}
          style={{ display: 'none' }}
        />
        {/* On the desktop table the cell is what has to be bounded, not the column: PrimeReact's table
            has no definite width, so it lays out by content and a column can never be narrower than
            its longest unbroken word — measured 645px for one wordy name, which squeezed every other
            column. Capping the cell (and letting the name truncate inside it) keeps the column in the
            range a name is expected to need, and it moves with the window rather than being frozen. */}
        <Col className={isMobileCard ? undefined : 'min-w-0 max-w-[clamp(10rem,18vw,26rem)]'}>
          <Link to={navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(row.id))}>
            <span style={{ fontWeight: 500 }}>{row.applicationId}</span>
          </Link>

          {/* The name is free text: it used to wrap over as many lines as it needed, and a single long
              word (nowhere to wrap) simply ran across the next column. It now gets two lines and is cut
              off after them, with the full name on hover — except on a mobile card, where there is no
              hover to fall back on, so there it keeps wrapping and only long words are broken up. */}
          {row.name && (isMobileCard ? <div className="break-words">{row.name}</div> : <EllipsisText lines={2}>{row.name}</EllipsisText>)}

          {row.supplierManifestId && (
            <Link to={navigation.createNavigationLink(navigation.urlFunctions.createSupplierManifestDetail(row.supplierManifestId))}>
              <span>{row.supplierManifestAppId}</span>
            </Link>
          )}
        </Col>
        <div className="text-[#999] text-[smaller]" data-testid={`customer-expeditions-row-${row.applicationId}-responsiblePerson`}>
          {row.responsiblePerson}
        </div>

        {row.supplierNotificationId && (
          <SupplierNotificationBadge supplierNotificationId={row.supplierNotificationId} supplierNotificationAppId={row.supplierNotificationAppId} />
        )}
        {!isMobileCard && (
          <SecuredArea
            id="row-labels" instance={row.applicationId}
            visible={[{ type: 'data', label: 'labels.length > 0', satisfied: !!row.labels && row.labels.length > 0 }]}
            label="Labels"
          >
            <ExpandableLabelWrapper labels={row.labels} isExpanded={expandedLabels} onToggle={toggleLabelsExpansion} />
          </SecuredArea>
        )}
        <SecuredArea
          id="row-ai-classification" instance={row.applicationId}
          visible={[aiClassificationFlag, { type: 'data', label: 'row.aiChatClassification present', satisfied: !!row.aiChatClassification }]}
          label="AI chat classification badge" description="AI classification of the messages exchanged about the shipment"
        >
          <div onClick={() => toggleExpandedRow?.(row)}>
            <AiChatClassificationButton aiChatClassification={row.aiChatClassification} />
          </div>
        </SecuredArea>
      </>
    );
  };

  const routeTemplate = (row: ExpeditionPreviewOutDTO, isMobileCard?: boolean) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div data-testid={`customer-expeditions-row-${row.applicationId}-route-from`}>{row.locations[0].name}</div>
        <div className="text-[#999] text-[smaller]" data-testid={`customer-expeditions-row-${row.applicationId}-route-from-timeslot`}>
          {formatTimeslotInterval(row.locations[0].timeslots)}
        </div>
        <COTimezone timezoneId={row.locations[0].timeslots.timezoneId} dataTestId={`customer-expeditions-row-${row.applicationId}-route-from-timezone`} />
        {row.locations.length > 1 && (
          <>
            <div style={{ marginTop: '0.5rem' }} data-testid={`customer-expeditions-row-${row.applicationId}-route-to`}>
              {row.locations[row.locations.length - 1].name}
            </div>
            <div className="text-[#999] text-[smaller]" data-testid={`customer-expeditions-row-${row.applicationId}-route-to-timeslot`}>
              {formatTimeslotInterval(row.locations[row.locations.length - 1].timeslots)}
            </div>
            <COTimezone
              timezoneId={row.locations[row.locations.length - 1].timeslots.timezoneId}
              dataTestId={`customer-expeditions-row-${row.applicationId}-route-to-timezone`}
            />
          </>
        )}
      </div>
    );
  };

  const stateAndManualModeTemplate = (row: ExpeditionPreviewOutDTO, isMobileCard?: boolean) => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <span data-testid={`customer-expeditions-row-${row.applicationId}-state`} data-expedition-status={row.expeditionStatus} style={{ display: 'none' }} />
        <ExpeditionStateSpan expeditionStage={row.expeditionStatus} isManual={row.isManualExpedition} />
        {row.isManualExpedition && (
          <div className="text-[#999] text-[smaller]" style={{ marginTop: '0.25rem' }}>
            {tr(`AllExpedition.manual`, `Manual`)}
          </div>
        )}
      </div>
    );
  };

  const isLadenFactorEligible = (orders: OrderPreviewExpeditionListOutDTO[]) => {
    if (orders.length !== 1) return false;

    const order = orders[0];
    return Array.isArray(order.items) && order.items.length === 1;
  };

  const getTotalHandlingUnits = (orders: OrderPreviewExpeditionListOutDTO[]) => {
    return orders.flatMap(order => order.items || []).reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  /** `compact`: the v3 table's 52px actions column, where the v2 cell's right margin would clip the button. */
  const navBodyTemplate = (rowData: ExpeditionPreviewOutDTO, compact = false) => {
    const shouldShowExtraCosts: boolean =
      rowData.expeditionStatus !== ExpeditionStatusEnum.IMPORTED &&
      rowData.expeditionStatus !== ExpeditionStatusEnum.NEW &&
      rowData.expeditionStatus !== ExpeditionStatusEnum.ASSIGNING &&
      rowData.expeditionStatus !== ExpeditionStatusEnum.CANCELED &&
      !(rowData.isManualExpedition && !isNullOrUndefined(rowData.expeditionToId));

    return (
      <div className={compact ? 'flex justify-center' : `flex items-center gap-2 mr-4 ${isMobile ? 'justify-start' : 'flex-col justify-between'}`}>
        {isMobile && (
          <SecuredArea id="row-duplicate" instance={rowData.applicationId} visible={expeditionListRwFlag} label="Duplicate shipment (mobile)" description="Duplicate the shipment">
            <Button
              label={tr('AllExpedition.duplicate', 'Duplikovat')}
              className="p-button-sm p-button-outlined !text-[#616061] !border-[#B2B2B2]"
              onClick={() => setDuplicateExpedition(rowData)}
              tooltip={tr('AllExpedition.duplicateTooltip', 'Vytvořit kopii této expedice')}
              tooltipOptions={{ position: 'top' }}
              style={{ boxShadow: '0 12px 16px rgba(254, 80, 0, 0.1)' }}
            />
          </SecuredArea>
        )}
        {!isMobile && (
          <SecuredArea id="row-more-actions" instance={rowData.applicationId} visible={expeditionListRwFlag} label="Shipment more-actions menu" description="Menu with more actions on the shipment">
            <ExpeditionMoreButtons
              expedition={{
                id: rowData.id,
                customerId: rowData.companyId,
                applicationId: rowData.applicationId,
                currency: rowData.currency,
                expeditionStatus: rowData.expeditionStatus,
                isArchived: rowData.isArchived,
                isFavorite: rowData.isFavorite,
                isDistributionCreated: isNotNullOrUndefined(rowData.distribution),
                linkedToSN: isNotNullOrUndefined(rowData.supplierNotificationId),
                firstLocationName: rowData.locations[0].name,
                assignedDispatcherEmail: rowData.carrierDispatcherEmail,
                carrierApiType: rowData.apiType,
                duplicationOptions: {
                  ladenFactorEligible: isLadenFactorEligible(rowData.orders),
                  handlingUnitCount: getTotalHandlingUnits(rowData.orders),
                  originalLadenFactor: rowData.ladenFactor,
                  originalAllowPartialDelivery: rowData.allowPartialDelivery,
                },
                showExtraCosts: shouldShowExtraCosts,
              }}
              onReload={() => tableRef.current?.reloadData()}
            />
          </SecuredArea>
        )}
      </div>
    );
  };

  const handleFileClick = (attachmentId: string, fileName: string) => {
    if (downloadAttachment) {
      console.log('Attachment download initiated for ID:', attachmentId);
      downloadAttachment(attachmentId, (data: { mimeType: string; data: string }) => {
        downloadBase64File(data.data, data.mimeType, fileName);
      });
    } else {
      console.warn('downloadAttachment function is not available in ApiContext');
    }
  };

  const getTransportTypeName = (type: string) => {
    const item = transportationTypeLov.find(i => i.transportationType === type);
    return item ? item.name : type;
  };

  const additionalInfoTemplate = (row: ExpeditionPreviewOutDTO, isMobileCard?: boolean) => {
    const handleTagClick = (key: InfoTagEnum) => {
      toggleInfoExpansion(key);
    };

    const handleAllTagClick = (key: InfoTagEnum) => {
      setAllTagsExpanded(!expandedInfoTags[key]);
    };

    const orderCount = row.orders?.length || 0;
    const stopCount = row.locations.length - 2;
    const stops =
      row.locations
        ?.slice(1, -1)
        .map(loc => loc.name)
        .join(', ') || '';
    const ordersTooltipContent =
      row.orders
        ?.map((order: OrderPreviewExpeditionListOutDTO) => {
          const itemsList = order.items
            ?.filter(item => item.name)
            ?.map((item: CargoPreviewPlanOutDTO) => `${item.name}: ${item.amount}`)
            ?.join(', ');

          return order.orderName ? `${order.orderName}${itemsList ? ' - ' + itemsList : ''}` : '';
        })
        ?.join(' | ') || '';
    const handlingUnitCount = getTotalHandlingUnits(row.orders || []);
    const unitsByType = (row.orders || [])
      .flatMap(order => order.items || [])
      .reduce<Map<string, number>>((acc, item: CargoPreviewPlanOutDTO) => {
        if (!item.name) return acc;
        acc.set(item.name, (acc.get(item.name) || 0) + (item.amount || 0));
        return acc;
      }, new Map<string, number>());
    const unitsSummary = Array.from(unitsByType.entries())
      .map(([name, amount]) => `${name}: ${amount}`)
      .join(', ');
    const unitsText = unitsSummary ? `${tr('AllExpedition.unitsTagLabel', 'Units: ')}${unitsSummary}` : undefined;
    const extraCostsTooltipContent = row.extraCosts?.map(ecItem => `${ecItem.name}: ${formatPrice(ecItem.price, ecItem.currency)}`).join('; ') || '';
    const carInfo: CarInfoPreviewOutDTO | undefined = row.carInfo;
    const carInfoText = [row.assignedDriver, carInfo?.carPlate].filter(v => isNotBlank(v)).join(', ');
    const expeditionType = !row.expeditionType ? null : tr(`global.expeditionTypeShort${row.expeditionType}`, row.expeditionType);

    const iaAdditionalInfoAvailable =
      row.isFavorite ||
      expeditionType ||
      row.createdByFullName ||
      (row.orders && orderCount > 0) ||
      isNotBlank(row.note) ||
      (expeditionExtraCostsFlag.granted && row.extraCosts && row.extraCosts.length > 0) ||
      (row.carInfo && isNotBlank(carInfoText)) ||
      stopCount > 0 ||
      row.isInvoiced ||
      (row.attachmentInfo && row.attachmentInfo.attachmentSize > 0) ||
      row.pairingSymbol ||
      row.trackingNumber ||
      row.transportationType ||
      row.aiDispatcherLastChangeAt;

    return (
      <div className="flex flex-col gap-2 items-start">
        <div className="flex flex-wrap gap-2 items-start">
          <SecuredArea
            id="row-tag-all" instance={row.applicationId}
            visible={[{ type: 'data', label: 'any additional info available', satisfied: !!iaAdditionalInfoAvailable }]}
            label="Expand-all tag"
          >
            <ExpandableInfoAllTag
              id={`all_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-all`}
              onClick={() => handleAllTagClick(InfoTagEnum.ALL)}
              tooltipText={expandedInfoTags['all'] ? tr(`expandableInfoTag.collapseAll`, 'Collapse all') : tr(`expandableInfoTag.expandAll`, 'Expand all')}
              icon={<SvgChevron className={clsx('text-content-brand', expandedInfoTags['all'] && 'rotate-180')} />}
            />
          </SecuredArea>
          <SecuredArea id="row-tag-favorite" instance={row.applicationId} visible={[{ type: 'data', label: 'isFavorite', satisfied: row.isFavorite }]} label="Favorite tag">
            <ExpandableInfoTag
              id={`favorite_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-favorite`}
              expanded={expandedInfoTags[InfoTagEnum.FAVORITE]}
              onClick={() => handleTagClick(InfoTagEnum.FAVORITE)}
              label={tr('AllExpedition.favorite', 'Favorite')}
              icon={<SvgStar className={'text-yellow-400 fill-current'} />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-type" instance={row.applicationId}
            visible={[{ type: 'data', label: 'expeditionType present', satisfied: !!expeditionType }]}
            label="Expedition type tag"
          >
            <ExpandableInfoTag
              id={`type_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-type`}
              expanded={expandedInfoTags[InfoTagEnum.TYPE]}
              onClick={() => handleTagClick(InfoTagEnum.TYPE)}
              iconText={expeditionType}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-created-by" instance={row.applicationId}
            visible={[{ type: 'data', label: 'createdByFullName present', satisfied: !!row.createdByFullName }]}
            label="Created by tag"
          >
            <ExpandableInfoTag
              id={`createdBy_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-createdBy`}
              expanded={expandedInfoTags[InfoTagEnum.CREATED_BY]}
              onClick={() => handleTagClick(InfoTagEnum.CREATED_BY)}
              label={tr('AllExpedition.createdByLabel', 'Created by: ')}
              text={row.createdByFullName}
              icon={<SvgUser />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-orders" instance={row.applicationId}
            visible={[{ type: 'data', label: 'orders.length > 0', satisfied: !!row.orders && orderCount > 0 }]}
            label="Orders tag"
          >
            <ExpandableInfoTag
              id={`orders_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-orders`}
              expanded={expandedInfoTags[InfoTagEnum.ORDERS]}
              onClick={() => handleTagClick(InfoTagEnum.ORDERS)}
              label={tr('AllExpedition.ordersTagLabel', 'Orders: ')}
              text={ordersTooltipContent}
              icon={<SvgOrder />}
              count={orderCount}
              secondaryCount={handlingUnitCount}
              extraText={unitsText}
            />
          </SecuredArea>

          <SecuredArea id="row-tag-note" instance={row.applicationId} visible={[{ type: 'data', label: 'note present', satisfied: isNotBlank(row.note) }]} label="Note tag">
            <ExpandableInfoTag
              id={`note_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-noteForCarrier`}
              expanded={expandedInfoTags[InfoTagEnum.NOTE]}
              onClick={() => handleTagClick(InfoTagEnum.NOTE)}
              label={tr('AllExpedition.noteTagLabel', 'Note: ')}
              text={noteToPlainText(row.note)}
              textComponent={<SafeHtml html={row.note} />}
              icon={<SvgFile />}
            />
          </SecuredArea>

          <SecuredArea
            id="row-extra-costs" instance={row.applicationId}
            visible={[expeditionExtraCostsFlag, { type: 'data', label: 'extraCosts.length > 0', satisfied: !!row.extraCosts && row.extraCosts.length > 0 }]}
            label="Extra costs tag" description="Extra costs of the shipment"
          >
            <ExpandableInfoTag
              id={`extraCosts_${enc(row.applicationId)}`}
              expanded={expandedInfoTags[InfoTagEnum.EXTRA_COSTS]}
              onClick={() => handleTagClick(InfoTagEnum.EXTRA_COSTS)}
              label={tr('AllExpedition.extraCostsTagLabel', 'Extra Costs: ')}
              text={extraCostsTooltipContent}
              icon={<SvgMoney />}
              count={row.extraCosts?.length ?? 0}
            />
          </SecuredArea>

          <SecuredArea
            id="row-tag-car-info" instance={row.applicationId}
            visible={[{ type: 'data', label: 'carInfo + driver/plate present', satisfied: !!row.carInfo && isNotBlank(carInfoText) }]}
            label="Car info tag"
          >
            <ExpandableInfoTag
              id={`carInfo_${enc(row.applicationId)}`}
              expanded={expandedInfoTags[InfoTagEnum.CAR_INFO]}
              onClick={() => handleTagClick(InfoTagEnum.CAR_INFO)}
              label={tr('AllExpedition.carInfoTagLabel', 'Car info: ')}
              text={carInfoText}
              icon={<SvgTruck />}
            />
          </SecuredArea>

          <SecuredArea
            id="row-tag-stops" instance={row.applicationId}
            visible={[{ type: 'data', label: 'intermediate stops > 0', satisfied: stopCount > 0 }]}
            label="Stops tag"
          >
            <ExpandableInfoTag
              id={`stops_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-stops`}
              expanded={expandedInfoTags[InfoTagEnum.STOPS]}
              onClick={() => handleTagClick(InfoTagEnum.STOPS)}
              label={tr('AllExpedition.stopsTagLabel', 'Stops: ')}
              text={stops}
              icon={<SvgRoute />}
              count={stopCount}
            />
          </SecuredArea>

          <SecuredArea id="row-tag-invoiced" instance={row.applicationId} visible={[{ type: 'data', label: 'isInvoiced', satisfied: row.isInvoiced }]} label="Invoiced tag">
            <ExpandableInfoTag
              id={`invoiced_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-invoicingState`}
              expanded={expandedInfoTags[InfoTagEnum.INVOICED]}
              onClick={() => handleTagClick(InfoTagEnum.INVOICED)}
              label={tr('AllExpedition.invoiced', 'Invoiced')}
              icon={<SvgCheckSquare className="text-complementary-success" />}
            />
          </SecuredArea>

          <SecuredArea
            id="row-tag-customs-status" instance={row.applicationId}
            visible={[{ type: 'data', label: 'customsStatus present', satisfied: !!row.customsStatus }]}
            label="Customs status tag"
          >
            <ExpandableInfoTag
              id={`customsStatus_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-customsStatus`}
              expanded={expandedInfoTags[InfoTagEnum.CUSTOMS_STATUS]}
              onClick={() => handleTagClick(InfoTagEnum.CUSTOMS_STATUS)}
              label={tr('AllExpedition.customsTagLabel', 'Customs: ')}
              text={row.customsStatus ? tr(`CustomsStatusTag.${row.customsStatus}`, row.customsStatus) : undefined}
              icon={<SvgCustoms />}
            />
          </SecuredArea>

          <SecuredArea
            id="row-tag-attachments" instance={row.applicationId}
            visible={[{ type: 'data', label: 'attachmentSize > 0', satisfied: !!row.attachmentInfo && row.attachmentInfo.attachmentSize > 0 }]}
            label="Attachments tag"
          >
            <ExpandableInfoTag
              id={`attachments_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-attachments`}
              expanded={expandedInfoTags[InfoTagEnum.ATTACHMENTS]}
              onClick={() => handleTagClick(InfoTagEnum.ATTACHMENTS)}
              icon={<SvgLink className={expandedInfoTags[InfoTagEnum.ATTACHMENTS] ? 'text-content-brand' : ''} />}
              label={tr('AllExpedition.attachmentTagLabel', 'Attachments: ')}
              count={row.attachmentInfo?.attachmentSize ?? 0}
              text={row.attachmentInfo?.attachments?.map(attachment => truncateFileName(attachment.name)).join(' | ')}
              textComponent={
                <span className="font-medium">
                  {row.attachmentInfo?.attachments?.map((attachment, idx) => (
                    <React.Fragment key={attachment.id}>
                      <AttachmentLink
                        id={attachment.id}
                        name={attachment.name}
                        onActivate={(id, fileName) => {
                          handleFileClick(id, fileName);
                        }}
                        maxLength={15}
                      />
                      {idx < row.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">{'\u00A0|\u00A0'}</span>}
                    </React.Fragment>
                  ))}
                </span>
              }
            />
          </SecuredArea>

          <SecuredArea
            id="row-tag-transport-legs" instance={row.applicationId}
            visible={[{ type: 'data', label: 'bestQuoteTransportLegs.length > 0', satisfied: !!row.bestQuoteTransportLegs && row.bestQuoteTransportLegs.length > 0 }]}
            label="Transport legs tag"
          >
            <ExpandableInfoTag
              id={`transport_legs_${enc(row.applicationId)}`}
              expanded={expandedInfoTags[InfoTagEnum.TRANSPORT_LEGS]}
              onClick={() => handleTagClick(InfoTagEnum.TRANSPORT_LEGS)}
              icon={<SvgLayers />}
              label={tr(`SpotQuoteDataTable.transportLegs`, 'Transport legs: ')}
              text={row.bestQuoteTransportLegs?.map(leg => joinLegInfo(leg)).join(' | ')}
              textComponent={row.bestQuoteTransportLegs?.map((leg, idx) => (
                <div key={idx}>
                  <div className="flex flex-row gap-2 mt-2">
                    <div className="flex items-start justify-center pt-0.5">{getTransportIcon(leg.transportationType, 3)}</div>
                    <div className="flex flex-col text-content-primary font-dm-sans font-normal">
                      {tr('carrier.demand.quote.transportationType.' + leg.transportationType.toLowerCase() + '.prefix', `${leg.transportationType}: `)}
                      {joinLegInfo(leg)}
                    </div>
                  </div>
                </div>
              ))}
              count={row.bestQuoteTransportLegs?.length ?? 0}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-pairing-symbol" instance={row.applicationId}
            visible={[{ type: 'data', label: 'pairingSymbol present', satisfied: !!row.pairingSymbol }]}
            label="Pairing symbol tag"
          >
            <ExpandableInfoTag
              id={`pairingSymbol_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-pairingSymbol`}
              expanded={expandedInfoTags[InfoTagEnum.PAIRING_SYMBOL]}
              onClick={() => handleTagClick(InfoTagEnum.PAIRING_SYMBOL)}
              label={tr('AllExpedition.pairingSymbol', 'Pairing symbol')}
              text={row.pairingSymbol}
              icon={<SvgNumber />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-tracking-number" instance={row.applicationId}
            visible={[{ type: 'data', label: 'trackingNumber present', satisfied: !!row.trackingNumber }]}
            label="Tracking number tag"
          >
            <ExpandableInfoTag
              id={`trackingNumber_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-trackingNumber`}
              expanded={expandedInfoTags[InfoTagEnum.TRACKING_NUMBER]}
              onClick={() => handleTagClick(InfoTagEnum.TRACKING_NUMBER)}
              label={tr('AllExpedition.trackingNumber', 'Tracking number')}
              text={row.trackingNumber}
              icon={<SvgNumber />}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-shipment-type" instance={row.applicationId}
            visible={[{ type: 'data', label: 'transportationType present', satisfied: !!row.transportationType }]}
            label="Shipment type tag"
          >
            <ExpandableInfoTag
              id={`shipmentType_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-shipmentType`}
              expanded={expandedInfoTags[InfoTagEnum.SHIPMENT_TYPE]}
              onClick={() => handleTagClick(InfoTagEnum.SHIPMENT_TYPE)}
              label={tr('AllExpedition.shipmentTypeTagLabel', 'Přeprava: ')}
              text={getTransportTypeName(row.transportationType)}
              icon={getTransportIcon(row.transportationType as unknown as TransportationTypeEnum, 4)}
            />
          </SecuredArea>
          <SecuredArea
            id="row-tag-ai-dispatcher-change" instance={row.applicationId}
            visible={[{ type: 'data', label: 'aiDispatcherLastChangeAt present', satisfied: !!row.aiDispatcherLastChangeAt }]}
            label="AI Dispatcher change tag"
          >
            <ExpandableInfoTag
              id={`aiDispatcherChange_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-aiDispatcherChange`}
              expanded={expandedInfoTags[InfoTagEnum.AI_DISPATCHER_CHANGE]}
              onClick={() => handleTagClick(InfoTagEnum.AI_DISPATCHER_CHANGE)}
              text={`${tr('AllExpedition.aiDispatcherFirstChange', 'First AI Dispatcher change')}: ${stringDateTimeWithLocaleFromApiStringInTz(row.aiDispatcherFirstChangeAt, timezoneIdToUse)}`}
              extraText={`${tr('AllExpedition.aiDispatcherLastChange', 'Last AI Dispatcher change')}: ${stringDateTimeWithLocaleFromApiStringInTz(row.aiDispatcherLastChangeAt, timezoneIdToUse)}`}
              icon={<SvgRobot />}
            />
          </SecuredArea>
        </div>
      </div>
    );
  };

  const tenderColumnTemplate = (row: ExpeditionPreviewOutDTO, isMobileCard?: boolean) => {
    const renderProgressBar = (offers: number, total: number, color?: string) => {
      if (total === 0) return null;
      const percent = total > 0 ? (offers / total) * 100 : 0;
      return (
        <div className="flex items-center gap-2 w-full text-sm">
          <span>
            {offers} {tr('AllExpedition.responsesShort', 'odpov.')}
          </span>
          <div className="bg-[#e5e7eb] h-2 w-[90px] relative shrink-0 overflow-hidden">
            <div className="h-2 transition-[width] duration-300" style={{ width: `${percent > 100 ? 100 : percent}%`, background: color || '#22c55e' }} />
          </div>
          <span>{tr('AllExpedition.fromTotal', 'z {{count}}', { count: total })}</span>
        </div>
      );
    };

    let content = null;
    const carInfo: CarInfoPreviewOutDTO | undefined = row.carInfo;
    const carInfoText = [row.assignedDriver, carInfo?.carPlate].filter(v => isNotBlank(v)).join(', ');

    if (row.assignedCarrierName) {
      const totalPrice = row.expeditionPrice + (row.extraCosts?.reduce((a, b) => a + b.price, 0) ?? 0);
      content = (
        <>
          <span
            data-testid={`customer-expeditions-row-${row.applicationId}-assigned-price`}
            data-assigned-total-price={String(totalPrice)}
            data-assigned-currency={row.currency ?? ''}
            style={{ display: 'none' }}
          />
          <SecuredArea id="row-price" instance={row.applicationId} visible={expeditionPriceFlag} label="Shipment price" description="Price of the shipment, including extra costs">
            <PriceTag
              priceDto={(() => {
                const priceDtos = [row.expeditionPriceDTO, ...(row.extraCosts?.flatMap(cost => cost.priceDTO) ?? [])].filter(p => p != null) as PriceDTO[];
                return priceDtos.length > 0 ? sumPriceDTOs(priceDtos) : undefined;
              })()}
            />
          </SecuredArea>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <SvgTruck />
            <span data-testid={`customer-expeditions-row-${row.applicationId}-assigned-carrierName`}>{row.assignedCarrierName}</span>
          </div>
          {isNotBlank(carInfoText) && (
            <span className="tag-text" data-testid={`customer-expeditions-row-${row.applicationId}-car-info`}>
              {carInfoText}
            </span>
          )}
        </>
      );
    } else if (row.isManualExpedition && row.expeditionToId && row.distribution && row.distribution.quotesCount > 0) {
      content = (
        <>
          <div>{tr('AllExpedition.replacedByManual', 'Nahrazeno manuální expedicí')}</div>
          {renderProgressBar(row.distribution.quotesOfferCount, row.distribution.quotesCount, '#FBBF24')}
        </>
      );
    } else if (row.expeditionStatus === ExpeditionStatusEnum.ASSIGNING && row.distribution && row.distribution.quotesCount > 0) {
      const validUntilDateDayjs = dayjsFromApiUtcInTz(row.distribution.utcEnd, timezoneIdToUse);
      content = (
        <>
          {row.distribution.isExpiredDistribution ? (
            <div className="text-[#ef4444] flex items-center gap-1 text-base">
              <AlertCircle width={16} height={16} style={{ marginRight: '0.25rem' }} />
              {validUntilDateDayjs && validUntilDateDayjs.isValid()
                ? tr('AllExpedition.tenderExpired', 'Vypršelo {{date}}', { date: validUntilDateDayjs.format('DD.MM.YYYY') })
                : tr('AllExpedition.tenderExpiredInvalidDate', 'Vypršelo (neplatné datum)')}
            </div>
          ) : (
            <div>
              {validUntilDateDayjs && validUntilDateDayjs.isValid()
                ? tr('AllExpedition.tenderValidUntil', 'Platnost do {{date}}', { date: validUntilDateDayjs.format('DD.MM HH:mm') })
                : tr('AllExpedition.tenderValidUntilInvalidDate', 'Platnost do (neplatné datum)')}
            </div>
          )}
          <COTimezone timezoneId={timezoneIdToUse} />
          {renderProgressBar(row.distribution.quotesOfferCount, row.distribution.quotesCount, '#22C55E')}
        </>
      );
    } else if (row.isManualExpedition) {
      content = (
        <div className="inline-block px-3 py-1 rounded border border-solid border-[#d1d5db] text-[#6b7280] text-sm font-medium leading-[1.2] text-center whitespace-nowrap bg-[#f9fafb]">
          {tr('AllExpedition.manualMode', 'Manuální mód')}
        </div>
      );
    } else if (!row.distribution || row.distribution.quotesCount === 0) {
      content = <div className="text-[#999] text-[smaller]">{tr('AllExpedition.noCarriersContacted', 'Nejsou oslovení dopravci')}</div>;
    } else {
      content = null;
    }

    return (
      <div
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem' }}
        data-testid={`customer-expeditions-row-${row.applicationId}-tender`}
        data-expired={row.distribution?.isExpiredDistribution ? 'true' : 'false'}
        data-valid-until-iso={row.distribution?.expiredUntil ?? ''}
        data-offers-count={String(row.distribution?.quotesOfferCount ?? '')}
        data-total-count={String(row.distribution?.quotesCount ?? '')}
      >
        {content}
        <span
          data-testid={`customer-expeditions-row-${row.applicationId}-tender-expired`}
          data-expired={row.distribution?.isExpiredDistribution ? 'true' : 'false'}
          style={{ display: 'none' }}
        />
        <span
          data-testid={`customer-expeditions-row-${row.applicationId}-tender-validUntil`}
          data-valid-until-iso={row.distribution?.expiredUntil ?? ''}
          style={{ display: 'none' }}
        />
        <span
          data-testid={`customer-expeditions-row-${row.applicationId}-tender-offersCount`}
          data-offers-count={String(row.distribution?.quotesOfferCount ?? '')}
          style={{ display: 'none' }}
        />
        <span
          data-testid={`customer-expeditions-row-${row.applicationId}-tender-totalCount`}
          data-total-count={String(row.distribution?.quotesCount ?? '')}
          style={{ display: 'none' }}
        />
      </div>
    );
  };

  const renderMobileCard = (row: ExpeditionPreviewOutDTO, checkbox?: JSX.Element | null) => {
    const { id, labels } = row;

    return (
      <div key={id} className="bg-white p-8 pb-6 shadow-[0_1px_0_0_rgba(229,231,235,1)] -mx-4 relative z-10 mb-1">
        <div className="flex items-start gap-3">
          {checkbox}
          <div className="flex-grow">{idAndResponsiblePersonTemplate(row, true)}</div>
        </div>

        {labels && labels.length > 0 && (
          <div className="mt-2">
            <MobileLabelsTemplate row={row} />
          </div>
        )}
        <div className="mt-3">{stateAndManualModeTemplate(row, true)}</div>
        <div className="mt-3">{routeTemplate(row, true)}</div>
        <div className="mt-3">{tenderColumnTemplate(row, true)}</div>
        <div className="mt-3">{additionalInfoTemplate(row, true)}</div>
        <div className="mt-3">{navBodyTemplate(row)}</div>
      </div>
    );
  };

  const MobileLabelsTemplate = ({ row }: { row: ExpeditionPreviewOutDTO }) => {
    return (
      <SecuredArea
        id="row-labels-mobile" instance={row.applicationId}
        visible={[{ type: 'data', label: 'labels.length > 0', satisfied: !!row.labels && row.labels.length > 0 }]}
        label="Labels (mobile)"
      >
        <ExpandableLabelWrapper labels={row.labels} isExpanded={expandedLabels} onToggle={toggleLabelsExpansion} />
      </SecuredArea>
    );
  };

  function getSeverityBorderColor(intent: IntentEnum) {
    switch (intent) {
      case IntentEnum.ISSUE:
        return 'border-complementary-error';
      case IntentEnum.QUESTION_OR_REQUEST:
        return 'border-complementary-warning';
      case IntentEnum.CONFIRMATION:
        return 'border-complementary-success';
      default:
        return 'border-content-gr2';
    }
  }

  const rowExpansionTemplate = (row: ExpeditionPreviewOutDTO, toggleExpandedRow?: (row: ExpeditionPreviewOutDTO) => void) => {
    return (
      <div className={`flex flex-col p-4 gap-4 rounded-lg border-[3px] border-solid bg-base-tertiary mx-auto ${getSeverityBorderColor(row.aiChatClassification.intent)}`}>
        <div className={`flex flex-row gap-4 justify-between items-center w-full`}>
          <div className={'flex flex-row gap-2 justify-start items-center'}>
            <span className="relative flex items-center">
              <SvgRobot
                className="w-6 h-6"
                onClick={() => {
                  toggleExpandedRow?.(row);
                }}
              />
              <div className={`absolute -right-0.5 -bottom-0.5`}>
                {React.createElement(intentEnumIcons[row.aiChatClassification.intent], {
                  width: 14,
                  height: 14,
                  className: getIntentIconColorClass(row.aiChatClassification.intent),
                })}
              </div>
            </span>
            <span className="text-content-primary font-dm-sans text-[16px] font-medium not-italic leading-normal tracking-[-0.16px]">
              {tr('AllExpedition.aiChatClassificationDetectedByAITitle', 'Detected alerts: {{number}}', { number: 1 })}:
            </span>
          </div>
          <SvgClose
            className="w-6 h-6 text-content-brand"
            onClick={() => {
              toggleExpandedRow?.(row);
            }}
          />
        </div>
        <div className={'flex px-4 flex-col justify-center items-start gap-[2px] flex-[1_0_0]'}>
          <div className={'flex flex-row justify-between items-center self-stretch py-4'}>
            <div className="flex flex-row items-center self-stretch text-content-primary font-dm-sans text-[14px] font-bold not-italic leading-normal">
              {t(`AllExpedition.aiChatClassificationIntent${row.aiChatClassification.intent}`, row.aiChatClassification.intent)}
              {row.aiChatClassification.topic && (
                <>
                  <span>:&nbsp;</span>
                  <span className="text-content-primary text-[14px] font-normal not-italic leading-normal">
                    {t(`AllExpedition.aiChatClassificationTopic${row.aiChatClassification.topic}`, row.aiChatClassification.topic)}
                  </span>
                </>
              )}
            </div>
            <Link to={navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(row.id, true))}>
              <RingilButton label={tr('AllExpedition.aiChatClassificationDetailButton', 'Detail')} onClick={() => {}} mode={'outlined-rectangle'} />
            </Link>
          </div>
        </div>
        <div className="flex px-4 text-content-secondary font-dm-sans text-[8px] font-normal not-italic leading-[12px]">
          {tr('AllExpedition.aiChatClassificationDetailDisclaimer', 'The AI classification is provided for informational purposes only and may not always be accurate.')}
        </div>
      </div>
    );
  };

  // --- Option builders (rebuilt each render so options track groupedFilters) ---
  const lovOptions = (lovs?: { id: string; name: string; active?: boolean }[]): OptionType[] =>
    (lovs ?? []).map(l => ({ label: l.name, value: l.id, active: l.active ?? true }));

  const countryOptions = (codes?: { name: string; code: string }[]): OptionType[] =>
    (codes ?? []).map(c => ({ label: c.name, value: c.code, active: true, icon: <CountryFlag code={c.code} /> }));

  const enumOptions = (all: string[], present: string[] | undefined, label: (v: string) => string): OptionType[] => {
    const restrict = Array.isArray(present) && present.length > 0;
    return all.filter(v => !restrict || present!.includes(v)).map(v => ({ label: label(v), value: v, active: true }));
  };

  const transportOptions = (): OptionType[] => {
    const all = transportationTypeLov.map(item => ({
      label: item.name,
      value: item.transportationType as unknown as string,
      active: true,
      icon: getTransportIcon(item.transportationType as unknown as TransportationTypeEnum, 4),
    }));
    if (groupedFilters === null) return all;
    return all.filter(o => groupedFilters.transportationType.map(String).includes(String(o.value)));
  };

  // Memoized so the (potentially large) option arrays + their icon JSX are only rebuilt
  // when the underlying data changes — not on every render (e.g. row selection).
  const filtersConf: FilterConfig[] = useMemo(() => {
    const labelOptions: OptionType[] = [
      { label: tr('generic.withoutLabel', 'Without label'), value: NO_LABEL_FILTER_VALUE, active: true },
      ...(groupedFilters?.labelValues ?? []).map(l => ({
        label: l.labelValue,
        value: l.labelId,
        active: true,
        icon: <span className="inline-block w-[14px] h-[14px] rounded-sm" style={{ backgroundColor: l.labelColor }} />,
      })),
    ];
    return [
      {
        filterType: FilterType.InputText,
        placeholder: tr('AllExpedition.enterExpeditionId', 'ID expedice'),
        value: ['applicationId'],
        defaultShown: true,
        dataTestId: 'customer-expeditions-filter-applicationId',
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.status', 'Status'),
        value: ['statusEnums'],
        defaultShown: true,
        options: enumOptions(Object.values(ExpeditionStatusEnum).filter(v => typeof v === 'string') as string[], groupedFilters?.statusEnums?.map(String), v =>
          tr(`ExpeditionStatusEnum.${v}`, v),
        ),
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.responsiblePerson', 'Odpovědná osoba'),
        value: ['responsiblePersonIds'],
        defaultShown: true,
        options: lovOptions(groupedFilters?.responsiblePersons ?? responsiblePersons),
      },
      { filterType: FilterType.MultiSelect, placeholder: tr('InputLabel.labels', 'Labels'), value: ['labelIds'], options: labelOptions },
      {
        filterType: FilterType.Calendar,
        placeholder: tr('AllExpedition.loadingDate', 'Nakládka'),
        value: ['loadingDateFromISO', 'loadingDateToISO'],
        dateTimeFormat: DateTimeFormatType.DateString,
      },
      {
        filterType: FilterType.Calendar,
        placeholder: tr('AllExpedition.unloadingTime', 'Vykládka'),
        value: ['unloadingDateFromISO', 'unloadingDateToISO'],
        dateTimeFormat: DateTimeFormatType.DateString,
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.createdBy', 'Vytvořil'),
        value: ['createdByIds'],
        options: lovOptions(groupedFilters?.createdByUsers),
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.carrier', 'Dopravce'),
        value: ['carrierIds'],
        options: lovOptions(groupedFilters?.carrierIds ?? carriers),
      },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.allLocationName', 'Všechny lokace'), value: ['allLocationName'] },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.pairingSymbol', 'Pairing symbol'), value: ['pairingSymbol'] },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.trackingNumber', 'Tracking number'), value: ['trackingNumber'] },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.loadingLocationName', 'Starting location name'), value: ['loadingLocationName'] },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.unLoadingLocationName', 'Destination location name'), value: ['unLoadingLocationName'] },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('AllExpedition.loadingCountry', 'Loading country'),
        value: ['loadingCountryCode'],
        options: countryOptions(groupedFilters?.loadingCountryCodes),
      },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('AllExpedition.unloadingCountry', 'Unloading country'),
        value: ['unloadingCountryCode'],
        options: countryOptions(groupedFilters?.unloadingCountryCodes),
      },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('AllExpedition.country', 'Country'),
        value: ['countryCode'],
        options: countryOptions(groupedFilters?.countryCodes),
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.additionalInfo', 'Additional info'),
        value: ['expeditionType'],
        options: enumOptions(Object.values(ExpeditionTypeEnum).filter(v => typeof v === 'string') as string[], groupedFilters?.expeditionTypes?.map(String), v =>
          tr(`ExpeditionTypeEnum.${v}`, v),
        ),
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.transportTypes', 'Způsob dopravy'),
        value: ['transportationType'],
        options: transportOptions(),
      },
      { filterType: FilterType.MultiSelect, placeholder: tr('AllExpedition.folderIds', 'Složka'), value: ['folderIds'], options: lovOptions(groupedFilters?.folderIds) },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.mrnNumber', 'Mrn number'), value: ['mrnNumber'] },
      { filterType: FilterType.InputText, placeholder: tr('AllExpedition.referenceOrderId', 'Reference order'), value: ['referenceOrderId'] },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('AllExpedition.customsStatus', 'Customs status'),
        value: ['customsStatus'],
        options: enumOptions(Object.values(CustomsStatusEnum).filter(v => typeof v === 'string') as string[], groupedFilters?.customsStatus?.map(String), v =>
          tr(`CustomsStatusTag.${v}`, v),
        ),
      },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('AllExpedition.archivedState', 'Archived'),
        value: ['archivedState'],
        options: [
          { label: tr('AllExpedition.archivedStateNotArchived', 'Not archived'), value: ArchivedFilterStateEnum.NOT_ARCHIVED, active: true },
          { label: tr('AllExpedition.archivedStateArchived', 'Only archived'), value: ArchivedFilterStateEnum.ARCHIVED, active: true },
          { label: tr('AllExpedition.archivedStateAll', 'All'), value: ArchivedFilterStateEnum.ALL, active: true },
        ],
      },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('AllExpedition.reservationState', 'Missing reservation'),
        value: ['reservationState'],
        options: [
          { label: tr('AllExpedition.reservationStateWith', 'OK'), value: ReservationFilterStateEnum.WITH_RESERVATION, active: true },
          { label: tr('AllExpedition.reservationStateWithout', 'Reservation missing'), value: ReservationFilterStateEnum.WITHOUT_RESERVATION, active: true },
          { label: tr('AllExpedition.reservationStateAll', 'All'), value: ReservationFilterStateEnum.ALL, active: true },
        ],
      },
      ...(attachmentNameFilterFlag.granted
        ? [
            {
              filterType: FilterType.InputText,
              placeholder: tr('generic.attachmentName', 'Attachment name'),
              value: ['attachmentName'],
              dataTestId: 'customer-expeditions-filter-attachmentName',
            } as FilterConfig,
          ]
        : []),
      ...(internalInfoFilterFlag.granted
        ? [
            {
              filterType: FilterType.MultiSelect,
              placeholder: tr('AllExpedition.invoiceState', 'Invoice state'),
              value: ['invoiceStates'],
              options: [
                { label: tr('InternalInfoTab.InvoiceStateEnum.INVOICED', 'Invoiced'), value: InvoiceStateEnum.INVOICED, active: true },
                { label: tr('InternalInfoTab.InvoiceStateEnum.NON_INVOICED', 'Non-invoiced'), value: InvoiceStateEnum.NON_INVOICED, active: true },
                { label: tr('AllExpedition.invoiceStateEmpty', 'Empty'), value: NO_INVOICE_STATE_FILTER_VALUE, active: true },
              ],
              dataTestId: 'customer-expeditions-filter-invoiceStates',
            } as FilterConfig,
          ]
        : []),
      { filterType: FilterType.Checkbox, placeholder: tr('AllExpedition.showFavourite', 'Favourite'), value: ['showFavourite'] },
      ...(internalInfoFilterFlag.granted
        ? [
            {
              filterType: FilterType.Checkbox,
              placeholder: tr('AllExpedition.withoutPairingSymbol', 'Without pairing symbol'),
              value: ['withoutPairingSymbol'],
              dataTestId: 'customer-expeditions-filter-withoutPairingSymbol',
            } as FilterConfig,
          ]
        : []),
      ...(aiDispatcherFlag.granted
        ? [
            {
              filterType: FilterType.Checkbox,
              placeholder: tr('AllExpedition.aiDispatcherChanged', 'Changed by AI Dispatcher'),
              value: ['aiDispatcherChanged'],
              dataTestId: 'customer-expeditions-filter-aiDispatcherChanged',
            } as FilterConfig,
          ]
        : []),
    ];
  }, [groupedFilters, responsiblePersons, carriers, transportationTypeLov, attachmentNameFilterFlag.granted, internalInfoFilterFlag.granted, aiDispatcherFlag.granted, tr]);

  const columnConfig: ColumnConfig<ExpeditionPreviewOutDTO>[] = [
    {
      id: 'applicationId',
      field: 'applicationId',
      header: 'AllExpedition.expediceHeaderTitle',
      headerDefault: 'Expedice',
      sortable: true,
      isDefaultSort: true,
      order: 1,
      render: (row, toggleExpandedRow) => idAndResponsiblePersonTemplate(row, false, toggleExpandedRow),
    },
    {
      id: 'state',
      field: 'expeditionStatus',
      header: 'AllExpedition.stateHeaderTitle',
      headerDefault: 'Stav',
      order: 2,
      render: row => stateAndManualModeTemplate(row),
    },
    { id: 'route', header: 'AllExpedition.route', headerDefault: 'Trasa', order: 3, render: row => routeTemplate(row) },
    { id: 'tender', header: 'AllExpedition.tenderColumnHeader', headerDefault: 'Tendr', order: 4, render: row => tenderColumnTemplate(row) },
    { id: 'additionalInfo', header: 'AllExpedition.additionalInfo', headerDefault: 'Additional info', order: 5, render: row => additionalInfoTemplate(row) },
    { id: 'actions', header: '', headerDefault: '', width: '50px', order: 6, render: row => navBodyTemplate(row) },
    // Not a real column: `hidden` drops it from the table, but "Order by" options are derived from
    // columnConfig, so this is how a sort gains an option without a column to go with it.
    {
      id: 'createdAt',
      field: 'createdDateInUtc',
      header: 'AllExpedition.createdAt',
      headerDefault: 'Vytvořeno',
      sortable: true,
      hidden: true,
      order: 7,
      render: () => null,
    },
  ];

  // Beta SPLIT_VIEW: the v3 table with tabs, its columns built from the field catalogue.
  // Rebuilt on every render so the fields see the current expanded tags and labels.
  const expeditionFields = buildExpeditionFields({
    tr,
    detailLink: id => navigation.createNavigationLink(navigation.urlFunctions.createExpeditionDetailV2(id)),
    manifestLink: id => navigation.createNavigationLink(navigation.urlFunctions.createSupplierManifestDetail(id)),
    timezoneId: timezoneIdToUse,
    flags: { price: expeditionPriceFlag, extraCosts: expeditionExtraCostsFlag, aiClassification: aiClassificationFlag, aiDispatcher: aiDispatcherFlag },
    infoTags: { isExpanded: key => !!expandedInfoTags[key], toggle: toggleInfoExpansion, setAll: setAllTagsExpanded },
    labels: { expanded: expandedLabels, toggle: toggleLabelsExpansion },
    transportTypeName: getTransportTypeName,
    onAttachmentClick: handleFileClick,
  });
  const expeditionViews: TableViewsConfig<ExpeditionPreviewOutDTO> = {
    tableKey: TableKeyEnum.EXPEDITION_LIST,
    catalogue: expeditionFields,
    presets: EXPEDITION_PRESETS,
    defaultColumns: EXPEDITION_DEFAULT_COLUMNS,
    defaultSort: { sortField: 'applicationId', sortDirection: 'DESC' },
    trailing: [{ id: 'actions', header: '', headerDefault: '', width: '52px', sticky: 'right', render: row => navBodyTemplate(row, true) }],
    // Stored as the tab's name when it is created, so it is translated once, in the user's language at that moment.
    firstViewName: tr('generic.all', 'All'),
    // Old saved filters become tabs once, on the first visit. Best-effort and silent: a failed read
    // imports nothing (the user still gets "All") rather than an error about something they didn't ask for.
    importLegacy: () =>
      new Promise(resolve =>
        getUserExpeditionFilters(
          data => resolve((data?.filters ?? []).map(saved => ({ name: saved.name, filter: (saved.data ?? {}) as Record<string, unknown> }))),
          null,
          { onError: () => resolve([]), onValidationFailed: () => resolve([]) },
        ),
      ),
    groups: EXPEDITION_FIELD_GROUPS,
  };

  // Every action says why it cannot be used, not only the merge: a greyed-out button that explains
  // itself and three that stay silent is worse than none of them explaining anything. These four
  // ask nothing of the selection beyond it not being empty.
  const needsSelection = (what: string) => (selected: ExpeditionPreviewOutDTO[]) =>
    selected.length === 0 ? tr('AllExpedition.massActionNeedsSelection', 'Select at least one shipment to {{action}}.', { action: what }) : undefined;

  const { MultiduplicationSidebar, setVisible: setVisibleMultiduplication } = useCreateMultiduplicationSidebar();
  const [multiduplicationRows, setMultiduplicationRows] = useState<ExpeditionPreviewOutDTO[]>([]);

  const massActionConfig: MassActionConfig<ExpeditionPreviewOutDTO>[] = [
    {
      label: tr('AllExpedition.nahratDokumenty', 'Upload documents'),
      onClick: () => setVisibleUploadDialog(true),
      disabledReason: needsSelection(tr('AllExpedition.massActionUploadDocuments', 'upload documents to')),
    },
    {
      label: tr('AllExpedition.internalInformation', 'Internal information'),
      onClick: () => setVisibleMAExpeditionIntInfo(true),
      disabledReason: needsSelection(tr('AllExpedition.massActionEditInternalInfo', 'edit internal information on')),
    },
    {
      label: tr('AllExpedition.massAction', 'Mass action'),
      onClick: () => setVisibleMA(true),
      disabledReason: needsSelection(tr('AllExpedition.massActionRun', 'run a mass action on')),
    },
    {
      label: tr('AllExpedition.dachserExport', 'Dachser export'),
      onClick: () => setVisibleDachserExport(true),
      secured: () => ({ id: 'mass-action-dachser-export', label: 'Dachser export', description: 'Export the selected shipments to Dachser', visible: dachserExportFlag }),
      disabledReason: needsSelection(tr('AllExpedition.massActionDachserExport', 'export to Dachser')),
    },
    {
      label: tr('AllExpedition.mergeShipments', 'Merge shipments'),
      onClick: () => navigation.navigate(navigation.urlFunctions.createMergedExpedition(selectedMassActions().map(t => t.expeditionId))),
      secured: () => ({ id: 'mass-action-merge-shipments', label: 'Merge shipments', description: 'Merge the selected shipments into one', visible: mergeExpeditionsFlag }),
      // The backend takes 1-10 ids, and merging one shipment is a no-op. Only shipments that have
      // not reached a carrier yet can be merged, so nothing has to be un-assigned or un-booked.
      // Both the greying-out and its explanation come from the same rule, so they cannot drift.
      disabled: selected => mergeDisabledReason(selected, tr) !== undefined,
      disabledReason: selected => mergeDisabledReason(selected, tr),
    },
    {
      label: tr('AllExpedition.multiduplicate', 'Duplikovat'),
      onClick: () => {
        setMultiduplicationRows(tableRef.current?.selectedObjects ?? []);
        setVisibleMultiduplication(true);
      },
      disabled: selected => selected.length < 1 || selected.length > 100,
      secured: () => ({ id: 'mass-action-multiduplication', label: 'Duplicate shipments', description: 'Duplicate the selected shipments in bulk', visible: multiduplicationFlag }),
    },
  ];

  return (
    <>
      <div className={clsx("font-['DM Sans']", isMobile && '-mx-4 px-8 pt-8')}>
        {!isMobile && <BreadCrumbComponent items={[{ label: tr(`AllExpedition.allExpeditions`, `Všechny expedice`) }]} />}
        <div className="flex items-center">
          <Heading title={t(`AllExpedition.allExpeditions`, `Všechny expedice`)} headingVariant={'h1'} />
          <div>
            <SecuredArea id="expedition-list-create" visible={expeditionCreateFlag} label="Create / import shipment" description="Buttons that create a new shipment or import shipments">
              <div className="flex items-center">
                <RingilButton
                  label={t(`AllExpedition.newExpedition`, `Nová expedice`)}
                  icon={<SvgPlus />}
                  mode={'link'}
                  onClick={() => navigation.navigate(navigation.urlFunctions.createExpedition())}
                />

                <SecuredArea id="expedition-list-import" visible={shipmentImportFlag} label="Import shipments" description="Import shipments">
                  <>
                    <span className="inline-block w-0.5 h-4 bg-[#b0b0b0] mx-2 my-auto content-center" />
                    <RingilButton
                      label={t(`AllExpedition.newImport`, `New import`)}
                      icon={<SvgImportExpeditions />}
                      mode={'link'}
                      onClick={() => navigation.navigate(navigation.urlFunctions.createNewExpeditionImport())}
                    />
                  </>
                </SecuredArea>
              </div>
            </SecuredArea>
          </div>
        </div>
      </div>

      {/* Beta SPLIT_VIEW: the same list on the compact v3 table. Both get one set of props. */}
      <LegacyVersionSwitch
        surface={BetaSurface.SPLIT_VIEW}
        labels={{
          toNew: tr('AllExpedition.switchToNewTable', 'Switch to new table'),
          toLegacy: tr('AllExpedition.switchToLegacyTable', 'Switch to legacy table'),
        }}
      >
        {showLegacy => {
          const tableProps = {
            filtersConf,
            loadDataFromApi: loadDataApi,
            filterName: 'expedition_list',
            getSavedFilters: getUserExpeditionFilters as never,
            saveFilters: saveUserExpeditionFilters as never,
            deleteSavedFilter: deleteUserFilter,
            massActionConfig: expeditionListRwFlag.granted ? massActionConfig : undefined,
            renderMobileCard: isMobile ? renderMobileCard : undefined,
            rowExpansionTemplate,
            exportConfig: { entityType: EntityTypeEnum.EXPEDITION, entityLabel: 'Expeditions', mapFilter: normalizeExpeditionFilter, secured: expeditionExportFlag },
          };
          return showLegacy ? (
            <RingilDataTable ref={tableRef} columnConfig={columnConfig} {...tableProps} />
          ) : (
            <RingilDataTableV3
              ref={tableRef}
              views={expeditionViews}
              // Not every change in the detail beside the list reaches it (an added attachment does not), so the user can reload.
              enableReload
              // A tab can load its rows as the user scrolls instead of in pages ("Table settings" → Rows).
              infiniteScroll
              // The split layouts (profile menu → page layout) show the open shipment's detail beside or below the list.
              split={{
                renderDetail: (row, { onChanged }) => <ExpeditionDetail key={row.id} expeditionId={row.id} embedded onChanged={onChanged} />,
              }}
              {...tableProps}
            />
          );
        }}
      </LegacyVersionSwitch>

      <Toast ref={toast} />
      <MultiduplicationSidebar
        expeditions={multiduplicationRows}
        onCreated={() => {
          tableRef.current?.clear();
        }}
      />
      <UploadAttachmentDialog
        visible={visibleUploadDialog}
        onHide={() => {
          setVisibleUploadDialog(false);
        }}
        entityType={EntityTypeEnum.EXPEDITION}
        entityIds={selectedMassActions().map(t => t.expeditionId)}
        onAttached={count => {
          tableRef.current?.reloadData();
          setVisibleUploadDialog(false);
          toast.current.show({
            severity: 'success',
            summary: tr('AllExpedition.dokumentyBylyNahrany', 'Dokumenty byly nahrany'),
            detail: tr('AllExpedition.nahraneDokumenty', 'K expedidici byly nahrany {{count}} dokumenty', { count }, count),

            life: 3000,
          });
          tableRef.current?.clear();
        }}
      />
      <MassActionExpeditionUpdateInternalInformation
        onComplete={() => {
          tableRef.current?.clear();
          tableRef.current?.reloadData();
        }}
        selectedExpeditions={selectedMassActions().map(t => {
          return {
            expeditionId: t.expeditionId,
            applicationId: t.applicationId,
          };
        })}
      />
      <MassActionExpedition
        onComplete={() => {
          tableRef.current?.clear();
          tableRef.current?.reloadData();
        }}
        selectedExpeditions={selectedMassActions().map(t => {
          return {
            expeditionId: t.expeditionId,
            applicationId: t.applicationId,
            expeditionStatus: t.state,
            isManualExpedition: t.isManualExpedition,
          };
        })}
      />
      <MassActionDachser
        onComplete={() => {
          tableRef.current?.clear();
        }}
        selectedExpeditions={selectedMassActions().map(t => {
          return {
            expeditionId: t.expeditionId,
            applicationId: t.applicationId,
            fromLocation: t.locationFrom,
            toLocation: t.locationTo,
          };
        })}
      />
      {duplicateExpedition && (
        <DuplicateExpeditionDialog
          visible={true}
          applicationId={duplicateExpedition.applicationId}
          expeditionId={duplicateExpedition.id}
          isLinkedToSN={isNotNullOrUndefined(duplicateExpedition.supplierNotificationId)}
          onCancel={() => setDuplicateExpedition(null)}
        />
      )}
    </>
  );
};
export default ResponsiveExpeditionList;
